import { useQuery } from '@tanstack/react-query';
import { Element, type HTMLReactParserOptions } from 'html-react-parser';
import { ExternalLink } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { CenterFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Empty from '@/components/Empty/Empty';
import { MessageTextStyle } from '@/entities/hey-syncly';
import { ReportPublicDocumentHeader } from '@/features/(reports)/report';
import type { CategoryMentionListModalOptions } from '@/features/hey-syncly/ui/mention-list-modal/types';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { studioService } from '../api/service';
import { StudioItemSourceView } from '../components/SourceView';
import { StudioItemMarkdown } from '../components/StudioItemDetail/studio-item-markdown';

const PublicCategoryIcon = ({
  categoryId,
  itemId,
}: {
  categoryId: number;
  itemId: string;
}) => {
  const setNewModal = useDetailModalStore((state) => state.setNewModal);

  return (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        setNewModal<CategoryMentionListModalOptions>({
          alias: itemId,
          type: DETAIL_MODAL_TYPE.CATEGORY_MENTION_LIST,
          options: {
            item_id: itemId,
            category_id: categoryId,
          },
        });
      }}
      className="translate-y-0.5"
      outlined={false}
      icon={<ExternalLink className="size-3.5" />}
    />
  );
};

const PublicStudioItemDetailPage = () => {
  const { studio_item_alias } = useParams();

  if (!studio_item_alias) {
    throw new Error('studio_item_alias is required');
  }

  const { data, isLoading } = useQuery({
    queryKey: ['studio-item', studio_item_alias, 'public'],
    queryFn: () =>
      studioService.GetHeySynclyStudioItemPublic(studio_item_alias),
  });

  if (isLoading) {
    return (
      <CenterFlex height="100vh">
        <Spinner />
      </CenterFlex>
    );
  }

  if (!data) {
    return <Empty title="Report not found" description="Report not found" />;
  }

  const markdownOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.name === 'category') {
        const rawId = domNode.attribs.id || '';
        const cleanId = rawId.replace('/', '').trim();
        const categoryId = Number(cleanId);

        if (Number.isNaN(categoryId)) {
          return null;
        }

        return (
          <PublicCategoryIcon categoryId={categoryId} itemId={data.alias} />
        );
      }
    },
  };

  return (
    <div className="flex flex-col h-screen overflow-auto bg-background">
      <ReportPublicDocumentHeader
        title={data.title}
        date={getTimezoneDayjs(data.created_at).format(DATE_FORMAT.readable)}
      />
      <div className="flex flex-col items-center flex-1 gap-4 px-16 pt-4 pb-14">
        <div className="w-full max-w-4xl rounded-xl border border-border bg-background p-5">
          <StudioItemSourceView
            item={data}
            showLinkedChat={false}
            collapsiblePrompt
          />
        </div>
        <div className="w-full max-w-4xl">
          <MessageTextStyle className="p-2 rounded-3xl">
            <StudioItemMarkdown
              content={data.content.body}
              options={markdownOptions}
            />
          </MessageTextStyle>
        </div>
      </div>
    </div>
  );
};

export default PublicStudioItemDetailPage;
